import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

/**
 * HScroll — keeps its children on ONE horizontal line: scrollable, no
 * scrollbar, with direction-aware edge fades + chevron chips that appear only
 * when there's more to scroll that way (mirrors the settings tab bar).
 *
 * Below ``sm`` it's an ``overflow-x-auto`` track (so a crowded footer scrolls
 * instead of wrapping to a second line). From ``sm`` up it becomes a plain
 * ``overflow-visible`` row — desktop is wide enough to fit everything, and,
 * crucially, an ``overflow`` container forces ``overflow-y`` to clip, which
 * would cut off any upward-opening popover (e.g. a dropdown) nested inside it.
 * Keeping desktop visible means those popovers render normally there; on mobile
 * the nested popovers must anchor with ``position: fixed`` to escape the track.
 *
 * The edge fade is ``from-surface`` — use this on a surface-coloured background.
 */
export function HScroll({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // 1px slack absorbs sub-pixel rounding so a fully-scrolled track doesn't
    // leave a phantom arrow enabled.
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [update]);

  const nudge = (dir: -1 | 1) =>
    ref.current?.scrollBy({ left: dir * 120, behavior: "smooth" });

  return (
    <div className={`relative ${className}`}>
      {/* Arrows are mobile-only (desktop doesn't scroll — see the docstring). */}
      {canLeft && (
        <button
          type="button"
          onClick={() => nudge(-1)}
          aria-label="Scroll left"
          className="sm:hidden absolute inset-y-0 left-0 z-10 flex items-center pr-5
                     bg-gradient-to-r from-surface via-surface to-transparent"
        >
          <Chevron dir="left" />
        </button>
      )}

      <div
        ref={ref}
        className="flex items-center gap-2 overflow-x-auto no-scrollbar scroll-smooth
                   sm:overflow-visible [&>*]:shrink-0"
      >
        {children}
      </div>

      {canRight && (
        <button
          type="button"
          onClick={() => nudge(1)}
          aria-label="Scroll right"
          className="sm:hidden absolute inset-y-0 right-0 z-10 flex items-center pl-5
                     bg-gradient-to-l from-surface via-surface to-transparent"
        >
          <Chevron dir="right" />
        </button>
      )}
    </div>
  );
}

function Chevron({ dir }: { dir: "left" | "right" }) {
  return (
    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-space-20 text-space-40 shadow-sm">
      <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d={dir === "left" ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"}
        />
      </svg>
    </span>
  );
}
